import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { saveAs } from 'file-saver'
import { useAuthStore } from '@/stores/auth'
import { config } from '@/config/env'
import { authenticatedRequest } from '@/utils/api'

// Interface pour les données SCR
export interface SCRData {
  scrGlobalUp: number
  scrGlobalDown: number
  scrEquity: number
  scrEquityType1: number
  scrEquityType2: number
  scrInterestRateUp: number
  scrInterestRateDown: number
  scrCurrency: number
  scrProperty: number
  scrSpread: number
  scrConcentration: number
}

interface SCRMarketResult {
  globalMarketValuation: number
  data: SCRData
  computationDate?: string
  perimeter?: {
    valuationDate?: string
    perimeterID?: string
  }
  parameters?: unknown
  simulationID?: string | null
}

export interface SCRComputationPayload {
  globalSCRMarketResult: SCRData
  globalMarketValuation?: number
  perimeter?: {
    valuationDate?: string
    perimeterID?: string
  }
  parameters?: unknown
  simulationID?: string | null
}

interface BlobErrorBody {
  message?: string
  Message?: string
}

export const useSolvencyStore = defineStore('solvency', () => {
  // État des calculs
  const computingScr = ref(false)
  const parametersChangedUnsaved = ref(false)
  
  // État des résultats
  const scrDone = ref(false)
  const scrLoading = ref(false)
  const scrReady = ref(false)
  const showGreenTick = ref(false)
  
  // Données de résultats SCR
  const scrMarketResult = ref<SCRMarketResult | undefined>(undefined)
  const computationDate = ref<string | undefined>(undefined)
  const onSimulation = ref(false)
  
  // État des rapports
  const scrReportLoading = ref(false)
  const actuarisReportLoading = ref(false)
  const assuretatReportLoading = ref(false)
  const qrtReportLoading = ref(false)
  const archiveScrExists = ref(false)
  
  // Paramètres QRT
  const qrt = ref({
    bceColumns: true,
    beginDateQrts0901: undefined as string | undefined
  })

  // Actions
  const setComputingScr = (value: boolean) => {
    computingScr.value = value
  }
  
  const setScrMarketResult = (payload: SCRComputationPayload | undefined) => {
    if (payload === undefined) {
      scrMarketResult.value = undefined
      onSimulation.value = false
      computationDate.value = undefined
      scrDone.value = false
      scrLoading.value = false
      showGreenTick.value = false
    } else {
      // L'API retourne la structure : { globalSCRMarketResult: {...}, globalMarketValuation: number, ... }
      // On restructure pour que scrMarketResult.value.data contienne globalSCRMarketResult
      scrMarketResult.value = {
        data: payload.globalSCRMarketResult, // Les données SCR sont ici
        globalMarketValuation: payload.globalMarketValuation ?? 0,
        computationDate: new Date().toLocaleString(),
        perimeter: payload.perimeter,
        parameters: payload.parameters,
        simulationID: payload.simulationID,
      }
      onSimulation.value = payload.simulationID !== null
      const d = new Date()
      computationDate.value = d.toLocaleString()
      scrDone.value = true
      scrLoading.value = false
      showGreenTick.value = true
    }
  }
  
  const setScrDone = (value: boolean) => {
    scrDone.value = value
  }
  
  const setScrLoading = (value: boolean) => {
    scrLoading.value = value
  }
  
  const setShowGreenTick = (value: boolean) => {
    showGreenTick.value = value
  }
  
  const setArchiveScrExists = (value: boolean) => {
    archiveScrExists.value = value
  }
  
  // Computed pour la dernière date de calcul
  const lastComputationDate = computed(() => computationDate.value)

  const reset = () => {
    computingScr.value = false
    parametersChangedUnsaved.value = false
    scrDone.value = false
    scrLoading.value = false
    scrReady.value = false
    showGreenTick.value = false
    scrMarketResult.value = undefined
    computationDate.value = undefined
    onSimulation.value = false
    scrReportLoading.value = false
    actuarisReportLoading.value = false
    assuretatReportLoading.value = false
    qrtReportLoading.value = false
    archiveScrExists.value = false
    qrt.value = {
      bceColumns: true,
      beginDateQrts0901: undefined
    }
  }
  
  // Actions de génération de rapports
  const generateScrReport = async () => {
    const authStore = useAuthStore()
    const userID = authStore.accountIdentifier
    
    if (!userID) {
      console.error('Erreur: Utilisateur non authentifié')
      return { success: false, error: 'Utilisateur non authentifié' }
    }
    
    try {
      scrReportLoading.value = true
      
      const response = await authenticatedRequest({
        url: `${config.baseUrl}/api/reporting/scr-excel`,
        method: 'GET',
        params: { userID },
        responseType: 'blob',
        timeoutProfile: 'download',
      })
      
      const filename = response.headers['content-disposition']
        ?.split(';')
        .find((n: string) => n.includes('filename='))
        ?.replace('filename=', '')
        .trim() || 'rapport-scr.xlsx'
        
      saveAs(new Blob([response.data]), filename)
      return { success: true }
    } catch (error: unknown) {
      console.error('Erreur génération rapport SCR:', error)
      
      // Essayer d'extraire le message d'erreur du blob
      let errorMessage = 'Erreur lors de la génération du rapport SCR'
      const blob = typeof error === 'object' && error !== null && 'response' in error
        ? (error as { response?: { data?: unknown } }).response?.data
        : undefined

      if (blob instanceof Blob) {
        try {
          const text = await blob.text()
          const json = JSON.parse(text) as BlobErrorBody
          errorMessage = json.message || json.Message || errorMessage
        } catch {
          // Ignorer les erreurs de parsing
        }
      }
      
      // Vérifier si c'est une erreur de calculs manquants
      if (errorMessage.includes('Veuillez d\'abord lancer les calculs SCR') || 
          errorMessage.includes('Aucun résultat SCR')) {
        scrDone.value = false // Réinitialiser le flag
        return { success: false, error: 'Les calculs SCR doivent être relancés pour ce périmètre. Veuillez retourner à la page de calculs.' }
      }
      
      return { success: false, error: errorMessage }
    } finally {
      scrReportLoading.value = false
    }
  }
  
  const generateActuarisReport = async () => {
    const authStore = useAuthStore()
    const userID = authStore.accountIdentifier
    
    if (!userID) {
      console.error('Erreur: Utilisateur non authentifié')
      return { success: false, error: 'Utilisateur non authentifié' }
    }
    
    try {
      actuarisReportLoading.value = true
      
      const response = await authenticatedRequest({
        url: `${config.baseUrl}/api/reporting/actuaris`,
        method: 'GET',
        params: { userID },
        responseType: 'blob',
        timeoutProfile: 'download',
      })
      
      const filename = response.headers['content-disposition']
        ?.split(';')
        .find((n: string) => n.includes('filename='))
        ?.replace('filename=', '')
        .trim() || 'rapport-actuaris.xlsx'
        
      saveAs(new Blob([response.data]), filename)
      return { success: true }
    } catch (error) {
      console.error('Erreur génération rapport Actuaris:', error)
      return { success: false, error: 'Erreur lors de la génération du rapport Actuaris' }
    } finally {
      actuarisReportLoading.value = false
    }
  }
  
  const generateAssuretatReport = async () => {
    const authStore = useAuthStore()
    const userID = authStore.accountIdentifier
    
    if (!userID) {
      console.error('Erreur: Utilisateur non authentifié')
      return { success: false, error: 'Utilisateur non authentifié' }
    }
    
    try {
      assuretatReportLoading.value = true
      
      const response = await authenticatedRequest({
        url: `${config.baseUrl}/api/reporting/assuretat`,
        method: 'GET',
        params: { userID },
        responseType: 'blob',
        timeoutProfile: 'download',
      })
      
      const filename = response.headers['content-disposition']
        ?.split(';')
        .find((n: string) => n.includes('filename='))
        ?.replace('filename=', '')
        .trim() || 'rapport-assuretat.xlsx'
        
      saveAs(new Blob([response.data]), filename)
      return { success: true }
    } catch (error) {
      console.error('Erreur génération rapport Assuretat:', error)
      return { success: false, error: 'Erreur lors de la génération du rapport Assuretat' }
    } finally {
      assuretatReportLoading.value = false
    }
  }
  
  const generateQrtReport = async () => {
    const authStore = useAuthStore()
    const userID = authStore.accountIdentifier
    
    if (!userID) {
      console.error('Erreur: Utilisateur non authentifié')
      return { success: false, error: 'Utilisateur non authentifié' }
    }
    
    try {
      qrtReportLoading.value = true
      
      const response = await authenticatedRequest({
        url: `${config.baseUrl}/api/reporting/qrt`,
        method: 'GET',
        params: {
          userID,
          bceColumns: qrt.value.bceColumns,
          beginDateQrts0901: qrt.value.beginDateQrts0901,
        },
        responseType: 'blob',
        timeoutProfile: 'download',
      })
      
      const filename = response.headers['content-disposition']
        ?.split(';')
        .find((n: string) => n.includes('filename='))
        ?.replace('filename=', '')
        .trim() || 'rapport-qrt.xlsx'
        
      saveAs(new Blob([response.data]), filename)
      return { success: true }
    } catch (error) {
      console.error('Erreur génération rapport QRT:', error)
      return { success: false, error: 'Erreur lors de la génération du rapport QRT' }
    } finally {
      qrtReportLoading.value = false
    }
  }
  
  const setDefaultQrtDate = () => {
    if (scrMarketResult.value?.perimeter?.valuationDate) {
      const d = new Date(scrMarketResult.value.perimeter.valuationDate)
      const q = new Date(d)
      q.setDate(d.getDate() - 1)
      qrt.value.beginDateQrts0901 = q.toISOString().slice(0, 10)
    }
  }

  return {
    // State
    computingScr,
    parametersChangedUnsaved,
    scrDone,
    scrLoading,
    scrReady,
    showGreenTick,
    scrMarketResult,
    computationDate,
    onSimulation,
    scrReportLoading,
    actuarisReportLoading,
    assuretatReportLoading,
    qrtReportLoading,
    archiveScrExists,
    qrt,
    // Computed
    lastComputationDate,
    
    // Actions
    setComputingScr,
    setScrMarketResult,
    setScrDone,
    setScrLoading,
    setShowGreenTick,
    setArchiveScrExists,
    setDefaultQrtDate,
    generateScrReport,
    generateActuarisReport,
    generateAssuretatReport,
    generateQrtReport,
    reset,
  }
}, {
  persist: {
    key: 'arago-solvency',
    storage: sessionStorage,
    // Persister les résultats pour éviter de recalculer après F5
    pick: [
      'computingScr',
      'parametersChangedUnsaved',
      'scrDone',
      'scrMarketResult',
      'computationDate',
      'onSimulation',
      'archiveScrExists',
      'showGreenTick',
    ],
  },
})
